import { ReactElement, useEffect, useState } from 'react';

import { FirebaseError } from 'firebase/app';

import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';

import ErrorIcon from '@mui/icons-material/Error';
import RefreshIcon from '@mui/icons-material/Refresh';
import SecurityIcon from '@mui/icons-material/Security';
import WifiOffIcon from '@mui/icons-material/WifiOff';

interface GratiSystemErrorProps {
  error: Error | FirebaseError;
  onRetry?: () => void;
}

export default function GratiSystemError({ error, onRetry }: GratiSystemErrorProps): ReactElement {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const isFirebaseError = error instanceof FirebaseError;
  const errorCode = isFirebaseError ? error.code : '';

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const getErrorDetails = () => {
    // Network/Connectivity Issues
    if (errorCode === 'unavailable' || errorCode === 'failed-precondition') {
      if (isOffline) {
        return {
          title: "You're Offline",
          message:
            'You appear to not be connected to the internet. Please check your internet connection and try again.',
          icon: <WifiOffIcon />,
          action: (
            <Button
              startIcon={<RefreshIcon />}
              onClick={() => window.location.reload()}
              color="inherit"
              size="small"
              sx={{
                whiteSpace: 'nowrap',
                minWidth: 'fit-content',
              }}
            >
              Try again
            </Button>
          ),
        };
      }

      if (errorCode === 'unavailable') {
        return {
          title: 'Gratispports Unavailable',
          message:
            "We're having trouble reaching our servers. It's probably our fault. This might be temporary - please try again in a moment.",
          icon: <WifiOffIcon />,
          action: (
            <Button
              startIcon={<RefreshIcon />}
              onClick={() => window.location.reload()}
              color="inherit"
              size="small"
              sx={{
                whiteSpace: 'nowrap',
                minWidth: 'fit-content',
              }}
            >
              Try again
            </Button>
          ),
        };
      }

      if (errorCode === 'failed-precondition') {
        console.log('internal error', error);

        // Check if this is an index-related error
        const isIndexError =
          error.message.includes('requires an index') ||
          error.message.includes('indexes?create_composite=') ||
          error.message.includes('The query requires an index');

        if (isIndexError) {
          // Extract the index creation URL if present
          const indexUrlMatch = error.message.match(/https:\/\/console\.firebase\.google\.com\/[^\s]+/);
          const indexUrl = indexUrlMatch ? indexUrlMatch[0] : null;

          // Show detailed info only in development mode
          const isDevelopment = import.meta.env.DEV;

          if (isDevelopment) {
            return {
              title: 'Database Index Required',
              message:
                "This query requires a database index that hasn't been created yet. This is a one-time setup requirement. " +
                (indexUrl
                  ? "Click the link below to create the required index, then try your action again once it's built."
                  : 'Please contact support to create the required index.'),
              icon: <ErrorIcon />,
              action: indexUrl ? (
                <Button
                  startIcon={<RefreshIcon />}
                  onClick={() => window.open(indexUrl, '_blank')}
                  color="inherit"
                  size="small"
                  sx={{
                    whiteSpace: 'nowrap',
                    minWidth: 'fit-content',
                  }}
                >
                  Create Index
                </Button>
              ) : (
                <Button
                  startIcon={<RefreshIcon />}
                  onClick={() => window.location.reload()}
                  color="inherit"
                  size="small"
                  sx={{
                    whiteSpace: 'nowrap',
                    minWidth: 'fit-content',
                  }}
                >
                  Refresh page
                </Button>
              ),
            };
          } else {
            // Production: Show user-friendly message
            return {
              title: 'Internal Error',
              message:
                'We encountered an internal system error. Please try again in a moment. If the problem persists, please contact support.',
              icon: <ErrorIcon />,
              action: (
                <Button
                  startIcon={<RefreshIcon />}
                  onClick={() => window.location.reload()}
                  color="inherit"
                  size="small"
                  sx={{
                    whiteSpace: 'nowrap',
                    minWidth: 'fit-content',
                  }}
                >
                  Try again
                </Button>
              ),
            };
          }
        }

        return {
          title: 'System Error',
          message: error.message,
          icon: <ErrorIcon />,
          action: (
            <Button
              startIcon={<RefreshIcon />}
              onClick={() => window.location.reload()}
              color="inherit"
              size="small"
              sx={{
                whiteSpace: 'nowrap',
                minWidth: 'fit-content',
              }}
            >
              Refresh page
            </Button>
          ),
        };
      }
    }

    // Authentication Errors
    if (errorCode === 'permission-denied' || errorCode === 'unauthenticated') {
      return {
        title: 'Permission Denied',
        message:
          'Our server rejected your request. Verify you are logged in. If you believe you should have permission, please contact support admin@gratigolf.com.',
        icon: <SecurityIcon />,
        action: null,
      };
    }

    // Firestore Errors
    if (errorCode === 'not-found') {
      return {
        title: 'Page Not Found',
        message:
          "The requested data doesn't seem to exist. This can sometimes happen if your app is out of date. Please refresh the page and try again.",
        icon: <ErrorIcon />,
        action: (
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => window.location.reload()}
            color="inherit"
            size="small"
            sx={{
              whiteSpace: 'nowrap',
              minWidth: 'fit-content',
            }}
          >
            Refresh page
          </Button>
        ),
      };
    }

    // Generic Error
    return {
      title: 'Something Went Wrong',
      message:
        "We're sorry, but something unexpected happened. Please try again in a moment. If the problem persists, please contact support admin@gratigolf.com.",
      icon: <ErrorIcon />,
      action: onRetry ? (
        <Button
          startIcon={<RefreshIcon />}
          onClick={onRetry}
          color="inherit"
          size="small"
          sx={{
            whiteSpace: 'nowrap',
            minWidth: 'fit-content',
          }}
        >
          Try again
        </Button>
      ) : (
        <Button
          startIcon={<RefreshIcon />}
          onClick={() => window.location.reload()}
          color="inherit"
          size="small"
          sx={{
            whiteSpace: 'nowrap',
            minWidth: 'fit-content',
          }}
        >
          Refresh page
        </Button>
      ),
    };
  };

  const { title, message, icon, action } = getErrorDetails();

  return (
    <Alert severity="error" icon={icon} action={action} sx={{ width: '100%' }}>
      <AlertTitle>{title}</AlertTitle>
      {message}
    </Alert>
  );
}
